import { Typography, Box, Stack, LinearProgress } from "@mui/material";
import { useCallback, useEffect } from "react";
import { OmmuseClient } from "../../proto/api_grpc_web_pb";
import { SelectField, styles } from "./style";
import { GenresArray } from "../../components/constants";
import { useState } from "react";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { GetUsernamesRequest } from "../../proto/api_pb";
import { User, UserSearchResponse } from "../../proto/api_pb";
import { backOff } from "exponential-backoff";
import { SelectContainer } from "./style";
import { GridContainer, StyledCard } from "../../components/ExploreBase/styles";
import { StyledCardMedia } from "../../components/ExploreBase/styles";
import { StyledCardActionArea } from "../../components/ExploreBase/styles";
import { NavLink } from "react-router-dom";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { FONT_FAMILY, TEXT_COLOR } from "../../theme";
import IconButton from "@mui/material/IconButton";
import ClearIcon from "@mui/icons-material/Clear";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const ExploreArtists: React.FC = () => {
  const [featuredUsers, setFeaturedUsers] = useState<UserSearchResponse[]>([]);
  const [regularUsers, setRegularUsers] = useState<UserSearchResponse[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [featuredUserSearch, setFeaturedUserSearch] = useState<
    UserSearchResponse[]
  >([]);
  const [query, setQuery] = useState<string>("");
  const [regularUserSearch, setRegularUserSearch] = useState<
    UserSearchResponse[]
  >([]);
  const [genres, setGenres] = useState<string[]>([]);
  const { getValidAccessToken } = useGetValidAccessToken();

  // handling all the search conditions
  const handleSearch = (query: string, genres: string[]) => {
    if (query !== "" && genres.length !== 0) {
      const featuredUsersResult = featuredUsers.filter((el) => {
        const username = el.getUsername().toLowerCase();
        const genreCheck = genres.some((element) =>
          el.getGenresList().includes(element)
        );
        return username.includes(query.toLowerCase()) && genreCheck;
      });
      const regularUsersResult = regularUsers.filter((el) => {
        const username = el.getUsername().toLowerCase();
        const genreCheck = genres.some((element) =>
          el.getGenresList().includes(element)
        );
        return username.includes(query.toLowerCase()) && genreCheck;
      });
      setFeaturedUserSearch(featuredUsersResult);
      setRegularUserSearch(regularUsersResult);
    } else if (query !== "" && genres.length === 0) {
      const featuredUsersResult = featuredUsers.filter((el) => {
        const username = el.getUsername().toLowerCase();
        return username.includes(query.toLowerCase());
      });
      const regularUsersResult = regularUsers.filter((el) => {
        const username = el.getUsername().toLowerCase();
        return username.includes(query.toLowerCase());
      });
      setFeaturedUserSearch(featuredUsersResult);
      setRegularUserSearch(regularUsersResult);
    } else if (query === "" && genres.length !== 0) {
      const featuredUsersResult = featuredUsers.filter((el) =>
        genres.some((element) => el.getGenresList().includes(element))
      );
      const regularUsersResult = regularUsers.filter((el) =>
        genres.some((element) => el.getGenresList().includes(element))
      );
      setFeaturedUserSearch(featuredUsersResult);
      setRegularUserSearch(regularUsersResult);
    } else {
      setFeaturedUserSearch(featuredUsers);
      setRegularUserSearch(regularUsers);
    }
  };

  const handleQueryChange = (e: any) => {
    const query = e.target.value;
    setQuery(query);
    handleSearch(query, genres);
  };

  const handleClearSearch = () => {
    setQuery("");
    handleSearch("", genres);
  };

  const handleChangeGenre = (
    _event: React.SyntheticEvent<Element, Event>,
    value: unknown
  ) => {
    setIsLoading(true);
    const values = value as Array<string>;
    setGenres(values);
    handleSearch(query, values);
    setIsLoading(false);
  };
  const defaultProfilePhoto = "assets/images/logo512.png";

  // Sort based on empty URL
  const customSort = (a: User, b: User): number => {
    if (a.getProfilePicture() === "" && b.getProfilePicture() === "") {
      // If both have empty profile pic URLs, leave them in the current order
      return 0;
    } else if (a.getProfilePicture() === "") {
      // Move objects with empty profile pic URLs to the end
      return 1;
    } else if (b.getProfilePicture() === "") {
      return -1;
    } else {
      // Compare non-empty profile pic URLs
      return a.getProfilePicture().localeCompare(b.getProfilePicture());
    }
  };

  const fetchUsersData = useCallback(async () => {
    setIsLoading(true);
    try {
      const accessToken = await getValidAccessToken();
      const ommuseClient = new OmmuseClient(process.env.REACT_APP_API_URL!);
      const request = new GetUsernamesRequest();
      const metadata = {
        Authorization: `${accessToken}`,
        "Access-Control-Allow-Origin": "*",
      };

      await backOff(
        () =>
          new Promise((resolve, reject) => {
            ommuseClient.getUsernames(request, metadata, function (er, res) {
              if (er) {
                console.log(er);
                setIsLoading(false);
                reject(er);
              } else {
                const featuredUserNameList = res.getFeaturedUsersList();
                const featuredList: Array<UserSearchResponse> = [];
                featuredUserNameList.map((user) => {
                  const u = new UserSearchResponse();
                  u.setUsername(user.getUsername());
                  u.setUserId(user.getUserId());
                  u.setSkillsList(user.getSkillsList());
                  u.setGenresList(user.getGenresList());
                  u.setProfilePicture(user.getProfilePicture());
                  featuredList.push(u);
                });
                setFeaturedUsers(featuredList);
                setFeaturedUserSearch(featuredList);
                const regularUserNameList = res.getRegularUsersList();
                regularUserNameList.sort(customSort);
                const regularList: Array<UserSearchResponse> = [];
                regularUserNameList.map((user) => {
                  const u = new UserSearchResponse();
                  u.setUsername(user.getUsername());
                  u.setUserId(user.getUserId());
                  u.setSkillsList(user.getSkillsList());
                  u.setGenresList(user.getGenresList());
                  u.setProfilePicture(user.getProfilePicture());
                  regularList.push(u);
                });
                setRegularUsers(regularList);
                setRegularUserSearch(regularList);
                setIsLoading(false);
                resolve(res);
              }
            });
          }),
        {
          numOfAttempts: 3,
          jitter: "full",
        }
      );
      setIsLoading(false);
    } catch (er) {
      console.log(er);
      setIsLoading(false);
    }
  }, [getValidAccessToken]);

  useEffect(() => {
    fetchUsersData();
  }, [fetchUsersData]);

  return (
    <div style={styles.outerContainer}>
      <h1 style={{ ...styles.heading, ...{ color: TEXT_COLOR } }}>
        Explore Artists From the OmMuse Community
      </h1>
      <SelectContainer>
        <Box className="row-one">
          <label
            style={{ ...styles.label, ...{ color: TEXT_COLOR } }}
            htmlFor="bio"
            className="label__input"
          >
            Search by username
          </label>
          <Box style={{ display: "flex" }}>
            <TextField
              onChange={(e) => handleQueryChange(e)}
              value={query}
              sx={styles.userSearch}
              id="outlined-basic"
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <IconButton
                    sx={{ visibility: query ? "visible" : "hidden" }}
                    onClick={handleClearSearch}
                  >
                    <ClearIcon />
                  </IconButton>
                ),
              }}
            />
          </Box>
        </Box>

        <Box className="row-one">
          <label
            style={{ ...styles.label, ...{ color: TEXT_COLOR } }}
            htmlFor="genre"
            className="label__input"
          >
            Select Genres
          </label>
          <Stack spacing={3}>
            <SelectField
              multiple
              sx={styles.selectField}
              id="checkboxes-tags-demo"
              options={GenresArray}
              disableCloseOnSelect
              onChange={(e, v) => handleChangeGenre(e, v)}
              renderOption={(props, option, { selected }) => (
                <li {...props} style={{ fontFamily: FONT_FAMILY }}>
                  <>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {option}
                  </>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label=" "
                  InputLabelProps={{ shrink: false }}
                />
              )}
            />
          </Stack>
        </Box>
      </SelectContainer>
      {!isLoading && featuredUserSearch.length !== 0 ? (
        <Box sx={{ width: "100%", padding: "20px" }}>
          <h2 style={styles.featuredHeading}>Featured Artists</h2>
          <GridContainer sx={{ marginTop: "10px" }}>
            {featuredUserSearch.map((item: UserSearchResponse) => {
              const userProfileRoute = `/user-profile/${item.getUsername()}`;
              return (
                <StyledCard key={item.getUserId()}>
                  <NavLink
                    to={userProfileRoute}
                    state={{ userId: item.getUserId() }}
                    style={styles.navLink}
                  >
                    <StyledCardActionArea>
                      <StyledCardMedia
                        // component="img"
                        image={item.getProfilePicture() || defaultProfilePhoto}
                        // alt="Album cover"
                      />
                    </StyledCardActionArea>
                    <div style={styles.userInfo}>
                      <div style={styles.userInfoLeftContainer}>
                        <p style={{ color: TEXT_COLOR }}>
                          {item.getUsername()}
                        </p>
                      </div>
                      <div
                        style={{
                          ...styles.userInfoRightContainer,
                          ...{ color: TEXT_COLOR },
                        }}
                      >
                        {item.getGenresList()[0]}
                      </div>
                    </div>
                  </NavLink>
                </StyledCard>
              );
            })}
          </GridContainer>
        </Box>
      ) : (
        <div></div>
      )}
      {!isLoading && regularUserSearch.length !== 0 && (
        <Box sx={{ width: "100%", padding: "20px" }}>
          <h2 style={styles.featuredHeading}>Artists</h2>
          <GridContainer>
            {regularUserSearch.map((item: any) => {
              const userProfileRoute = `/user-profile/${item.getUsername()}`;
              return (
                <StyledCard key={item.getUserId()}>
                  <NavLink
                    to={userProfileRoute}
                    state={{ userId: item.getUserId() }}
                    style={styles.navLink}
                  >
                    <StyledCardActionArea>
                      <StyledCardMedia
                        // component="img"
                        image={item.getProfilePicture() || defaultProfilePhoto}
                        // alt="Album cover"
                      />
                    </StyledCardActionArea>
                    <div style={styles.userInfo}>
                      <div style={styles.userInfoLeftContainer}>
                        <p style={{ color: TEXT_COLOR }}>
                          {item.getUsername()}
                        </p>
                      </div>
                      <div
                        style={{
                          ...styles.userInfoRightContainer,
                          ...{ color: TEXT_COLOR },
                        }}
                      >
                        {item.getGenresList()[0]}
                      </div>
                    </div>
                  </NavLink>
                </StyledCard>
              );
            })}
          </GridContainer>
        </Box>
      )}
      {!isLoading &&
        regularUserSearch.length === 0 &&
        featuredUserSearch.length === 0 && (
          <Box style={styles.noUserContainer}>
            <Typography style={styles.note}>No user found</Typography>
          </Box>
        )}
      {isLoading ? (
        <Box sx={{ height: "30vh" }}>
          <LinearProgress sx={{ marginTop: "20vh" }} />
        </Box>
      ) : (
        <></>
      )}
    </div>
  );
};

export default ExploreArtists;
